// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomProductContextAvailability from "@tps/App/Components/Product/CustomProductContextAvailability";
import CustomProductImage from "@tps/App/Components/Product/CustomProductImage";
import ProductEmployeePartNumbers from "@tps/App/Components/Product/ProductEmployeePartNumbers";
import ProductShippingAndActions, {
    ProductShippingAndActionsStyle,
} from "@tps/App/Components/Product/ProductShippingAndActions";
import productDescriptionVariantStyles from "@tps/App/SharedStyles/ProductDescriptionStyles";
import { CustomProductContext, CustomProductContextModel } from "@tps/App/Store/Contexts/CustomProductContext";
import ProductDescription, { ProductDescriptionStyles } from "@tps/Overrides/Components/ProductDescription";
import IconStockRecommended from "@tps/XDL/Icons/IconStockRecommended";
import { pdpIconStyle } from "@tps/XDL/PreStyle/select";
import { xScreen } from "@tps/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isAuthenticated: state.context.session.isAuthenticated && !state.context.session.isGuest,
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    product: ProductModel;
    extendedStyles?: RelatedProductCardStyles;
    showBrandName: boolean;
    showAvailability: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    carouselId: string;
    recommendedQty: number | undefined;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface RelatedProductCardStyles {
    container?: InjectableCss;
    actionsSection?: InjectableCss;
    rightSection?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    qtyAndPriceWrapper?: InjectableCss;
    description?: TypographyProps;
    productPriceStyles?: ProductPriceStyles;
    imageWrapper?: InjectableCss;
    recommendedQtyWrapper?: InjectableCss;
    recommendedQtyTxt?: TypographyPresentationProps;
    productShippingAndActionsStyle?: ProductShippingAndActionsStyle;
    tabletRecommendedQtyWrapper?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    productNumber?: TypographyProps;
}

export const productDetailsProductCardStyles: RelatedProductCardStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    rightSection: {
        css: css`
            padding-left: 12px;
            flex-grow: 1;
            max-width: calc(100% - 80px);
            ${xScreen("lte", "md")} {
                max-width: calc(100% - 83px);
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 80px;
            ${xScreen("lte", "md")} {
                width: 83px;
            }
        `,
    },
    actionsSection: {
        css: css`
            width: 100%;
            margin-left: 80px;
            padding-left: 12px;
            ${xScreen("lte", "md")} {
                margin-left: 0;
                padding-left: 0;
            }
        `,
    },
    recommendedQtyTxt: {
        size: 12,
        lineHeight: "17px",
        css: css`
            margin-left: 4px;
        `,
    },
    recommendedQtyWrapper: {
        css: css`
            display: flex;
            align-items: center;
            ${IconWrapper} {
                margin-bottom: 1px;
            }
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            gap: 20px;
            margin-bottom: 8px;
        `,
    },
    productDescriptionStyles: {
        ...productDescriptionVariantStyles?.small,
    },
    productShippingAndActionsStyle: {
        signInWrapper: {
            css: css`
                margin-top: 0;
                ${xScreen("lte", "md")} {
                    margin-top: 8px;
                }
            `,
        },
        shippingDropDown: {
            cssOverrides: {
                formField: css`
                    ${xScreen("lte", "md")} {
                        max-width: unset;
                    }
                `,
                formInputWrapper: css`
                    max-width: 320px;
                    ${xScreen("lte", "md")} {
                        max-width: unset;
                    }
                `,
            },
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            ...pdpIconStyle,
        },
        productShippingAndActionsWrapper: {
            css: css`
                column-gap: 12px;
            `,
        },
    },
    productPriceStyles: {
        price: {
            priceText: {
                weight: "bold",
                size: 14,
                lineHeight: "19px",
                css: css`
                    margin-bottom: 0;
                `,
            },
            errorText: {
                weight: "bold",
                size: 14,
                lineHeight: "19px",
                css: css`
                    margin-bottom: 0;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 14,
                    lineHeight: "19px",
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
            realTimeText: {
                weight: "bold",
                size: 14,
                lineHeight: "19px",
            },
        },
    },
    tabletRecommendedQtyWrapper: {
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                margin-top: 4px;
                display: block;
            }
        `,
    },
};

const RelatedProductCard = ({
    product,
    extendedStyles,
    productInfo,
    isAuthenticated,
    showBrandName,
    showAvailability,
    showPrice,
    showAddToCart,
    showImageBadges,
    maximumNumberOfImageBadges,
    updateProductInfo,
    carouselId,
    recommendedQty,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productDetailsProductCardStyles, extendedStyles));
    const [warehouseId, setWarehouseId] = useState("");

    useEffect(() => {
        if (recommendedQty) {
            updateQuantity(recommendedQty);
        }
    }, []);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    const updateWarehouseId = (warehouseId: string) => {
        setWarehouseId(warehouseId);
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product: {
            ...product,
            properties: {
                ...product?.properties,
                warehouseId,
            },
        },
        productInfo: {
            ...productInfo,
        },
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
        onWarehouseChanged: updateWarehouseId,
    } as CustomProductContextModel;

    return (
        <>
            <CustomProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <CustomProductImage
                            showImage={true}
                            showImageBadges={showImageBadges}
                            maxImageBadges={maximumNumberOfImageBadges}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSection}>
                        {product.brand && showBrandName && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                        )}
                        <ProductDescription
                            redirectToParent={true}
                            product={productContext}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                        <ProductEmployeePartNumbers product={product} />
                        {isAuthenticated && (
                            <StyledWrapper {...styles.qtyAndPriceWrapper}>
                                {showPrice && (
                                    <ProductPrice
                                        showLabel={false}
                                        product={productContext}
                                        extendedStyles={styles.productPriceStyles}
                                        showUnitOfMeasure={true}
                                        showInvalidPriceMessage={true}
                                        showPack={false}
                                    />
                                )}
                                {showAddToCart && recommendedQty && (
                                    <StyledWrapper {...styles.recommendedQtyWrapper}>
                                        <Icon size={13} color="warning" src={IconStockRecommended} />
                                        <Typography {...styles.recommendedQtyTxt}>
                                            {`${translate("Recommended Quantity :")} ${recommendedQty}`}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        )}
                        {isAuthenticated && showAvailability && (
                            <CustomProductContextAvailability isProductDetailsPage={true} />
                        )}
                        {isAuthenticated && showAddToCart && recommendedQty && (
                            <StyledWrapper {...styles.tabletRecommendedQtyWrapper}>
                                <Icon size={13} color="warning" src={IconStockRecommended} />
                                <Typography {...styles.recommendedQtyTxt}>
                                    {`${translate("Recommended Quantity :")} ${recommendedQty}`}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.actionsSection}>
                        {showAddToCart && (
                            <ProductShippingAndActions
                                showNumberInCart={false}
                                showSpeedAbilityIcon={false}
                                extendedStyles={styles.productShippingAndActionsStyle}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </CustomProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RelatedProductCard);
